import {
  formatNdtLaudo,
  normalizeCommon,
  validateCommon,
  type NdtDocumentCommon,
  type NdtLaudo,
  type NdtReportCommon,
  type NdtValidationResult
} from "./ndt-report-common.js";
import { type LpIlluminationType, normalizeIlluminationType } from "./lp-report-domain.js";
import type { PmConsumableType, PmTechnique } from "./pm-consumable.js";

/**
 * Relatório de Ensaio por Partículas Magnéticas (PM) — FORM-PM REV.00.
 *
 * Seções do modelo: 01 Identificação · 02 Dados do Ensaio · 03 Equipamento e
 * Consumíveis · 04 Registro da Inspeção / Resultados · 05 Croqui ·
 * 06 Observações e Parecer Final · 07 Legenda e Critério de Resultado ·
 * 08 Evidências Fotográficas · 09 Termo de Responsabilidade & Signatários.
 */

export type PmSurfaceCondition = "LIXADA" | "PINTADA" | "OUTRA" | "";
export const PM_SURFACE_CONDITION_OPTIONS: ReadonlyArray<{ value: PmSurfaceCondition; label: string }> = [
  { value: "", label: "Selecione" },
  { value: "LIXADA", label: "Lixada" },
  { value: "PINTADA", label: "Pintada" },
  { value: "OUTRA", label: "Outra" }
];

export type PmCurrentType = "AC" | "DC" | "OUTRO" | "";
export const PM_CURRENT_TYPE_OPTIONS: ReadonlyArray<{ value: PmCurrentType; label: string }> = [
  { value: "", label: "Selecione" },
  { value: "AC", label: "AC" },
  { value: "DC", label: "DC" },
  { value: "OUTRO", label: "Outro" }
];

export type PmMagnetizationDirection = "LONGITUDINAL" | "TRANSVERSAL" | "CRUZADA" | "";
export const PM_DIRECTION_OPTIONS: ReadonlyArray<{ value: PmMagnetizationDirection; label: string }> = [
  { value: "", label: "Selecione" },
  { value: "LONGITUDINAL", label: "Longitudinal" },
  { value: "TRANSVERSAL", label: "Transversal" },
  { value: "CRUZADA", label: "Cruzada" }
];

export type PmIntensityUnit = "LUX" | "UW_CM2";
export const PM_INTENSITY_UNIT_OPTIONS: ReadonlyArray<{ value: PmIntensityUnit; label: string }> = [
  { value: "LUX", label: "Lux" },
  { value: "UW_CM2", label: "µW/cm²" }
];

export type PmFunctionalCheck = "APROVADO" | "REPROVADO" | "NAO_REALIZADA" | "";
export const PM_FUNCTIONAL_CHECK_OPTIONS: ReadonlyArray<{ value: PmFunctionalCheck; label: string }> = [
  { value: "", label: "Selecione" },
  { value: "APROVADO", label: "Aprovado" },
  { value: "REPROVADO", label: "Reprovado" },
  { value: "NAO_REALIZADA", label: "Não realizada" }
];

function optionLabel<T extends string>(options: ReadonlyArray<{ value: T; label: string }>, value: T | null | undefined): string {
  if (!value) return "";
  return options.find(o => o.value === value)?.label ?? String(value);
}
export const formatPmSurfaceCondition = (v: PmSurfaceCondition | null | undefined) => optionLabel(PM_SURFACE_CONDITION_OPTIONS, v);
export const formatPmCurrentType = (v: PmCurrentType | null | undefined) => optionLabel(PM_CURRENT_TYPE_OPTIONS, v);
export const formatPmDirection = (v: PmMagnetizationDirection | null | undefined) => optionLabel(PM_DIRECTION_OPTIONS, v);
export const formatPmIntensityUnit = (v: PmIntensityUnit | null | undefined) => optionLabel(PM_INTENSITY_UNIT_OPTIONS, v);
export const formatPmFunctionalCheck = (v: PmFunctionalCheck | null | undefined) => optionLabel(PM_FUNCTIONAL_CHECK_OPTIONS, v);

/** Consumível PM como o relatório o guarda: o vínculo mais o texto congelado (Marca / Produto / Lote / Validade / Técnica). */
export interface PmConsumableRef {
  consumableId: string | null;
  brand: string;
  product: string;
  lot: string;
  /** "aaaa-mm-dd" */
  validity: string;
  technique: PmTechnique | "";
}

export function emptyPmConsumableRef(): PmConsumableRef {
  return { consumableId: null, brand: "", product: "", lot: "", validity: "", technique: "" };
}

/** Os três consumíveis do FORM-PM (linhas 01–03 da tabela), pelo tipo do cadastro global. */
export const PM_CONSUMABLE_SLOTS: ReadonlyArray<{ field: "particles" | "contrastPaint" | "carrier"; type: PmConsumableType; label: string; required: boolean }> = [
  { field: "particles", type: "MAGNETIC_PARTICLES", label: "Partículas Magnéticas", required: true },
  { field: "contrastPaint", type: "CONTRAST_PAINT", label: "Tinta de Contraste", required: false },
  { field: "carrier", type: "CARRIER_SUSPENSION", label: "Veículo / Suspensão", required: false }
];

/** Linha de "04 REGISTRO DA INSPEÇÃO / RESULTADOS": Nº · JUNTA/ELEMENTO · LOCALIZAÇÃO · EXTENSÃO · INDICAÇÕES/ACHADOS · LAUDO. */
export interface PmResultItem {
  key: string;
  item: string;
  element: string;
  location: string;
  extension: string;
  findings: string;
  laudo: NdtLaudo;
}

export interface PmReport extends NdtReportCommon<PmDocumentModel> {
  // 02 — DADOS DO ENSAIO
  surfaceCondition: PmSurfaceCondition;
  surfaceConditionOther: string;
  /** Técnica / aplicação: Via seca / Via úmida / Visível / Fluorescente (códigos do cadastro de consumíveis PM). */
  technique: PmTechnique | "";
  magnetizationMethod: string;
  currentType: PmCurrentType;
  currentTypeOther: string;
  magnetizationDirection: PmMagnetizationDirection;
  /** mm, texto do usuário. */
  poleSpacing: string;
  illuminationType: LpIlluminationType;
  illuminationIntensity: string;
  illuminationUnit: PmIntensityUnit;
  technicalNote: string;

  // 03 — EQUIPAMENTO E CONSUMÍVEIS
  yokeAssetId: string | null;
  /** Marca / Modelo / Identificação, congelados na seleção. */
  yokeDescription: string;
  yokeSerialNumber: string;
  yokeCertificate: string;
  yokeCertificateDate: string;
  yokeCertificateValidity: string;
  functionalCheck: PmFunctionalCheck;
  functionalCheckNote: string;
  particles: PmConsumableRef;
  contrastPaint: PmConsumableRef;
  carrier: PmConsumableRef;

  // 04 — REGISTRO DA INSPEÇÃO
  results: PmResultItem[];
}

export interface PmDocumentModel extends NdtDocumentCommon {
  testData: {
    procedure: string;
    referenceNorm: string;
    acceptanceCriteria: string;
    baseMaterial: string;
    surfaceCondition: string;
    technique: string;
    magnetizationMethod: string;
    currentType: string;
    magnetizationDirection: string;
    poleSpacing: string;
    illuminationType: string;
    illuminationIntensity: string;
    testTemperature: string;
    technicalNote: string;
  };
  equipment: {
    yoke: string;
    certificate: string;
    serialNumber: string;
    functionalCheck: string;
  };
  consumables: Array<{ type: PmConsumableType; typeLabel: string; brand: string; product: string; lot: string; validity: string; technique: string; expired: boolean }>;
  results: Array<{ item: string; element: string; location: string; extension: string; findings: string; laudo: NdtLaudo; laudoLabel: string }>;
}

export function validatePmReportCompleteness(report: PmReport): NdtValidationResult {
  const errors = validateCommon(report, "PM");
  if (!report.technique) errors.push("Selecione a Técnica / Aplicação.");
  if (!report.currentType) errors.push("Selecione o Tipo de Corrente.");
  if (!report.magnetizationDirection) errors.push("Selecione a Direção / Sentido de magnetização.");
  if (!report.illuminationType) errors.push("Selecione o Tipo de Iluminação.");
  if (!report.yokeAssetId && !report.yokeDescription.trim()) errors.push("Selecione o Yoke / Equipamento PM.");
  if (!report.particles.consumableId && !report.particles.product.trim()) errors.push("Selecione as Partículas Magnéticas.");
  if (report.results.length === 0) {
    errors.push("Adicione ao menos um item no Registro da Inspeção.");
  } else {
    const withoutElement = report.results.filter(r => !r.element.trim()).length;
    if (withoutElement > 0) errors.push(`Existem ${withoutElement} ${withoutElement === 1 ? "item" : "itens"} sem Junta / Elemento informado.`);
    const withoutLaudo = report.results.filter(r => !r.laudo).length;
    if (withoutLaudo > 0) errors.push(`Existem ${withoutLaudo} ${withoutLaudo === 1 ? "item" : "itens"} sem Laudo.`);
  }
  return { valid: errors.length === 0, errors };
}

export function normalizePmReport(report: PmReport): PmReport {
  const r = report as any;
  normalizeCommon(r, "PM");
  if (!Array.isArray(r.results)) r.results = [];
  for (const slot of PM_CONSUMABLE_SLOTS) if (!r[slot.field]) r[slot.field] = emptyPmConsumableRef();
  r.illuminationType = normalizeIlluminationType(r.illuminationType);
  if (!r.illuminationUnit) r.illuminationUnit = "LUX";
  for (const key of ["surfaceCondition", "surfaceConditionOther", "technique", "magnetizationMethod", "currentType", "currentTypeOther", "magnetizationDirection", "poleSpacing", "illuminationIntensity", "technicalNote", "yokeDescription", "yokeSerialNumber", "yokeCertificate", "yokeCertificateDate", "yokeCertificateValidity", "functionalCheck", "functionalCheckNote"]) {
    if (typeof r[key] !== "string") r[key] = "";
  }
  if (r.yokeAssetId === undefined) r.yokeAssetId = null;
  r.results = r.results.map((raw: any, index: number) => ({
    key: raw.key || `PM-ITEM-LEGACY-${index}`,
    item: raw.item ?? String(index + 1).padStart(2, "0"),
    element: raw.element ?? "",
    location: raw.location ?? "",
    extension: raw.extension ?? "",
    findings: raw.findings ?? raw.description ?? "",
    laudo: raw.laudo ?? ""
  }));
  return report;
}

export { formatNdtLaudo as formatPmLaudo };
